import { history } from "@umijs/max";
import { Button, Result } from "antd";
import React from "react";

interface AuthGuardProps {
  children: React.ReactNode;
  menus: API.MenuDTO[];
  noAuthPaths?: string[];
}

/**
 * 权限守卫组件
 * 用于验证用户是否有权限访问当前页面
 */
const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  menus,
  noAuthPaths = [],
}) => {
  const { location } = history;
  const currentPath = location.pathname;

  // 检查是否需要权限验证
  const needAuth = !noAuthPaths.includes(currentPath);

  // 检查是否有权限
  if (needAuth) {
    // 如果菜单还未加载，拒绝访问
    if (menus.length === 0) {
      return (
        <Result
          status="403"
          title="403"
          subTitle="抱歉，您没有权限访问此页面"
          extra={
            <Button type="primary" onClick={() => history.push("/")}>
              返回首页
            </Button>
          }
        />
      );
    }

    const hasPermission = checkPagePermission(menus, currentPath);

    if (!hasPermission) {
      // 显示 403 页面
      return (
        <Result
          status="403"
          title="403"
          subTitle="抱歉，您没有权限访问此页面"
          extra={
            <Button type="primary" onClick={() => history.push("/")}>
              返回首页
            </Button>
          }
        />
      );
    }
  }

  return <>{children}</>;
};

/**
 * 检查用户是否有访问指定页面的权限
 */
function checkPagePermission(menus: API.MenuDTO[], pagePath: string): boolean {
  // 1. 先进行精确匹配和动态路由匹配
  if (hasExactPermission(menus, pagePath)) {
    return true;
  }

  // 2. 如果没有精确权限，检查是否是从属页面，可以继承父级模块权限
  return checkSubordinatePagePermission(menus, pagePath);
}

/**
 * 精确匹配权限（包括静态路径和动态路由）
 */
function hasExactPermission(menus: API.MenuDTO[], pagePath: string): boolean {
  for (const menu of menus) {
    // 精确匹配路径
    if (menu.path === pagePath) {
      return true;
    }

    // 支持动态路由匹配(如 /match/detail/:id)
    if (menu.path && menu.path.includes(":")) {
      const pattern = menu.path.replace(/:\w+/g, "[^/]+");
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(pagePath)) {
        return true;
      }
    }

    // 递归检查子菜单
    if (menu.children && menu.children.length > 0) {
      if (hasExactPermission(menu.children, pagePath)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * 检查从属页面权限（详情页、编辑页、创建页）
 * 这些页面可以继承父级模块的权限
 */
function checkSubordinatePagePermission(
  menus: API.MenuDTO[],
  pagePath: string,
): boolean {
  // 只对特定的从属页面类型进行父级权限检查
  const subordinatePagePatterns = [
    /\/detail\/[^/]+$/, // 详情页: /xxx/detail/:id
    /\/edit\/[^/]+$/,   // 编辑页: /xxx/edit/:id
    /\/create$/,        // 创建页: /xxx/create
  ];

  // 检查是否是从属页面
  const isSubordinatePage = subordinatePagePatterns.some((pattern) =>
    pattern.test(pagePath),
  );

  if (!isSubordinatePage) {
    // 不是从属页面，必须有精确权限
    return false;
  }

  // 提取模块前缀，例如 /match/detail/6 -> /match
  const pathSegments = pagePath.split("/").filter(Boolean);
  if (pathSegments.length < 2) {
    return false;
  }

  const modulePrefix = `/${pathSegments[0]}`;

  // 检查是否有该模块的任何权限
  return hasModulePermission(menus, modulePrefix);
}

/**
 * 递归检查是否有指定模块的权限
 */
function hasModulePermission(
  menus: API.MenuDTO[],
  modulePrefix: string,
): boolean {
  for (const menu of menus) {
    // 如果菜单路径以该模块开头，说明用户有该模块的权限
    if (menu.path && menu.path.startsWith(modulePrefix)) {
      return true;
    }

    // 递归检查子菜单
    if (menu.children && menu.children.length > 0) {
      if (hasModulePermission(menu.children, modulePrefix)) {
        return true;
      }
    }
  }

  return false;
}

export default AuthGuard;
